import { LoroText } from "loro-crdt";
import { useEffect, useRef } from "react";
import type { CodeLanguage } from "@kss/schema";
import { CODE_LANGUAGES, mountCodeMirror, type CmMount } from "../../editors/CodeMirrorView";
import { useLoroSelector } from "../../sync/hooks";
import type { BlockComponentProps } from "../registry";
import { bodyMap, ensureText } from "../textField";
import { useT } from "../../i18n";

/** Code block (spec §7.4.3, §8.8): CodeMirror bound to `body.source`; `language` is LWW. */
export function CodeBlock({ handle, node, readOnly, onExitUp, onExitDown, onFocus }: BlockComponentProps) {
  const t = useT();
  const ref = useRef<HTMLDivElement>(null);
  const mountRef = useRef<CmMount | null>(null);
  const body = bodyMap(node);
  const language = (useLoroSelector(handle.doc, () => (body?.get("language") as CodeLanguage | undefined) ?? "plaintext") ?? "plaintext") as CodeLanguage;

  useEffect(() => {
    const el = ref.current;
    if (!el || !body) return;
    const text = ensureText(body, "source");
    if (!(body.get("source") instanceof LoroText)) handle.doc.commit({ origin: "sys:init" });
    const m = mountCodeMirror(el, { doc: handle.doc, text, readOnly, language, onExitUp, onExitDown, onFocus });
    mountRef.current = m;
    return () => {
      mountRef.current = null;
      m.view.destroy();
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [handle.doc, node.id]);

  useEffect(() => {
    void mountRef.current?.setLanguage(language);
  }, [language]);
  useEffect(() => {
    mountRef.current?.setReadOnly(readOnly);
  }, [readOnly]);

  return (
    <div className="overflow-hidden rounded-md border border-neutral-200 bg-[var(--surface)] shadow-[var(--shadow-sm)] dark:border-neutral-800" data-testid="code-block">
      <div className="flex items-center gap-2 border-b border-neutral-200 bg-[var(--surface-2)] px-2.5 py-1 font-mono text-[11px] uppercase tracking-[0.06em] text-neutral-500 dark:border-neutral-800">
        <span>{t("code.label")}</span>
        <select
          className="bg-transparent"
          value={language}
          disabled={readOnly}
          data-testid="code-language"
          onChange={(e) => {
            body?.set("language", e.target.value as CodeLanguage);
            handle.doc.commit();
          }}
        >
          {CODE_LANGUAGES.map((l) => (
            <option key={l} value={l}>
              {l}
            </option>
          ))}
        </select>
      </div>
      <div ref={ref} />
    </div>
  );
}
